import {
  ModeCalculFraisParc,
  ModeCalculTarification,
  TypeBorne,
  TypeCommission,
  TypeEcheance,
  TypePrelevement,
  TypeTunnel,
  UnitEnum,
} from 'api/funnel';

/**
 * Libellés des énumérations du domaine tunnel.
 *
 * Source unique : l'ancien back-office les redéfinissait dans chaque écran, ce qui avait
 * fini par produire des libellés divergents pour un même code (« À terme » / « A terme »).
 */

export interface EnumOption<T> {
  value: T;
  label: string;
  /** Précision affichée en aide à la saisie. */
  hint?: string;
}

export const MODE_CALCUL_OPTIONS: EnumOption<ModeCalculTarification>[] = [
  { value: ModeCalculTarification.MontantFixe, label: 'Montant fixe TTC' },
  { value: ModeCalculTarification.MontantFixeHT, label: 'Montant fixe HT' },
  { value: ModeCalculTarification.Taux, label: 'Taux' },
  { value: ModeCalculTarification.Expression, label: 'Expression' },
];

export const TYPE_PRELEVEMENT_OPTIONS: EnumOption<TypePrelevement>[] = [
  {
    value: TypePrelevement.Unique,
    label: 'Unique',
    hint: 'Une seule fois, à la première cotisation',
  },
  {
    value: TypePrelevement.AEcheance,
    label: 'À échéance',
    hint: 'À chaque échéance annuelle du contrat',
  },
  {
    value: TypePrelevement.FractionneAnnuel,
    label: 'Fractionné annuel',
    hint: 'Montant annuel divisé selon le fractionnement',
  },
  {
    value: TypePrelevement.FractionneParPrelevement,
    label: 'Fractionné par prélèvement',
    hint: 'Montant appliqué à chaque prélèvement',
  },
];

export const TYPE_ECHEANCE_OPTIONS: EnumOption<TypeEcheance>[] = [
  { value: TypeEcheance.ATerme, label: 'À terme' },
  { value: TypeEcheance.Comptant, label: 'Comptant' },
  { value: TypeEcheance.LesDeux, label: 'Les deux' },
];

export const TYPE_TUNNEL_OPTIONS: EnumOption<TypeTunnel>[] = [
  {
    value: TypeTunnel.Individuel,
    label: 'Individuel',
    hint: 'Souscription directe, supports SOLO',
  },
  {
    value: TypeTunnel.Collectif,
    label: 'Collectif',
    hint: 'Souscription via un contrat de groupe, supports GROUP / CADRE',
  },
];

/**
 * Libellé d'un type de tunnel, `null` si la valeur n'appartient pas à l'énumération.
 *
 * Un tunnel dont le back n'a pas renseigné le type arrive à 0 : le rendre « Individuel »
 * par défaut afficherait une information fausse avec l'aplomb d'une information sûre.
 */
export const tunnelTypeLabel = (value?: number | null): string | null =>
  TYPE_TUNNEL_OPTIONS.find((option) => option.value === value)?.label ?? null;

export const TYPE_COMMISSION_OPTIONS: EnumOption<TypeCommission>[] = [
  { value: TypeCommission.Slider, label: 'Curseur' },
  { value: TypeCommission.List, label: 'Liste de valeurs' },
];

export const TYPE_BORNE_OPTIONS: EnumOption<TypeBorne>[] = [
  { value: TypeBorne.Slider, label: 'Curseur' },
  { value: TypeBorne.List, label: 'Liste de valeurs' },
];

export const UNIT_OPTIONS: EnumOption<UnitEnum>[] = [
  { value: UnitEnum.Amount, label: 'Montant (€)' },
  { value: UnitEnum.Day, label: 'Jours' },
  { value: UnitEnum.Week, label: 'Semaines' },
  { value: UnitEnum.Month, label: 'Mois' },
  { value: UnitEnum.Year, label: 'Années' },
];

/** Short unit shown next to a deductible or ceiling value. */
export const unitSuffix = (value?: UnitEnum | null): string => {
  switch (value) {
    case UnitEnum.Day:
      return 'jours';
    case UnitEnum.Week:
      return 'semaines';
    case UnitEnum.Month:
      return 'mois';
    case UnitEnum.Year:
      return 'années';
    default:
      return '€';
  }
};

export const MODE_CALCUL_FRAIS_PARC_OPTIONS: EnumOption<ModeCalculFraisParc>[] = [
  {
    value: ModeCalculFraisParc.ParBien,
    label: 'Calculé pour chaque bien',
    hint: 'Comportement par défaut : le frais est appliqué bien par bien puis sommé',
  },
  {
    value: ModeCalculFraisParc.UneFoisParParc,
    label: 'Une seule fois pour le parc',
    hint: 'Le frais est compté une seule fois, quel que soit le nombre de biens',
  },
];

/** Modes autorisés pour interpréter le montant parc (cf. SaveFraisParcConfigCommand). */
export const MODE_MONTANT_PARC_OPTIONS: EnumOption<ModeCalculTarification>[] = [
  { value: ModeCalculTarification.MontantFixeHT, label: '€ HT' },
  { value: ModeCalculTarification.Taux, label: '% de la prime du parc' },
];

const labelFrom = <T,>(options: EnumOption<T>[], value: T | undefined | null): string =>
  options.find((option) => option.value === value)?.label ?? '—';

export const modeCalculLabel = (value?: ModeCalculTarification | null) =>
  labelFrom(MODE_CALCUL_OPTIONS, value);

export const typePrelevementLabel = (value?: TypePrelevement | null) =>
  labelFrom(TYPE_PRELEVEMENT_OPTIONS, value);

export const typeEcheanceLabel = (value?: TypeEcheance | null) =>
  labelFrom(TYPE_ECHEANCE_OPTIONS, value);

export const modeCalculFraisParcLabel = (value?: ModeCalculFraisParc | null) =>
  labelFrom(MODE_CALCUL_FRAIS_PARC_OPTIONS, value);

/** Le mode de calcul détermine quel champ de montant est pertinent. */
export const isTauxMode = (mode?: ModeCalculTarification | null) =>
  mode === ModeCalculTarification.Taux;

export const isExpressionMode = (mode?: ModeCalculTarification | null) =>
  mode === ModeCalculTarification.Expression;

export const isMontantHTMode = (mode?: ModeCalculTarification | null) =>
  mode === ModeCalculTarification.MontantFixeHT;

export const isMontantTTCMode = (mode?: ModeCalculTarification | null) =>
  mode === ModeCalculTarification.MontantFixe;
